import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Button, AlertMessage } from "components/atoms";
import { PasswordInput } from "components/molecules";
import { useAuth } from "contexts";

const NewPasswordRequiredSchema = Yup.object().shape({
  newPassword: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/[0-9]/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*(),.?":{}|<>]/,
      "Password must contain at least one special character"
    )
    .required("Password is required"),
  confirmNewPassword: Yup.string()
    .oneOf([Yup.ref("newPassword"), undefined], "Passwords must match")
    .required("Confirm Password is required"),
});

const NewPasswordForm: React.FC = () => {
  const { setAuthState, handleSignInConfirmation } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleSuccessfulPasswordChange = (): void => {
    newPasswordRequiredFormik.resetForm();
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: "SIGN_IN",
    }));
  };

  const handlePasswordChangeError = (error: unknown): void => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    setErrorMessage(errorMessage);
  };

  const handleNewPasswordRequiredSubmit = async (values: {
    newPassword: string;
  }) => {
    try {
      const error = await handleSignInConfirmation(values.newPassword);

      if (error) {
        handlePasswordChangeError(error);
        return;
      }

      handleSuccessfulPasswordChange();
    } catch (error) {
      handlePasswordChangeError(error);
    }
  };

  const newPasswordRequiredFormik = useFormik({
    initialValues: {
      newPassword: "",
      confirmNewPassword: "",
    },
    validationSchema: NewPasswordRequiredSchema,
    onSubmit: handleNewPasswordRequiredSubmit,
  });

  const toSignIn = (): void => {
    setAuthState((prevState) => ({
      ...prevState,
      nextStep: "SIGN_IN",
    }));
  };

  return (
    <form
      className="auth-form"
      onSubmit={newPasswordRequiredFormik.handleSubmit}
    >
      <div className="form-group">
        <h2>Change Password</h2>
      </div>

      <PasswordInput
        label="Password"
        id="newPassword"
        placeholder="Enter your password"
        getFieldProps={newPasswordRequiredFormik.getFieldProps}
        touched={newPasswordRequiredFormik.touched.newPassword}
        error={newPasswordRequiredFormik.errors.newPassword}
      />

      <PasswordInput
        label="Confirm Password"
        id="confirmNewPassword"
        placeholder="Enter your password"
        getFieldProps={newPasswordRequiredFormik.getFieldProps}
        touched={newPasswordRequiredFormik.touched.confirmNewPassword}
        error={newPasswordRequiredFormik.errors.confirmNewPassword}
      />

      {errorMessage && <AlertMessage type="error">{errorMessage}</AlertMessage>}

      <Button
        type="submit"
        text="Change Password"
        className="form-button"
        width="100%"
        variant="primary"
      />

      <Button
        type="button"
        text="Back to Sign In"
        onClick={toSignIn}
        className="form-button"
        width="100%"
        variant="border"
      />
    </form>
  );
};

export default NewPasswordForm;
